/**
 * AppShellLayout — chrome PERSISTANT de l'app ERP authentifiée.
 *
 * Layout React Router (rendu via <Outlet />) commun à toutes les pages
 * authentifiées (Dashboard, Paramètres, futurs modules cave/ventes…). Il porte :
 *   - <RequireAuth> : redirection /login si la session n'est pas valide
 *   - Sidebar (navigation principale)
 *   - BubblesCanvas (animation de bulles en arrière-plan, densité réduite)
 *   - Topbar (recherche, notifications, avatar)
 *   - Zone scrollable contenant l'<Outlet />
 *
 * Pourquoi ce layout : avant, chaque page (Dashboard, DomaineSettings…)
 * dupliquait son propre shell — sidebar, topbar, bulles, fond. À chaque
 * navigation Dashboard ↔ Paramètres tout se démontait/remontait, et le
 * BubblesCanvas perdait son état (positions, vitesses des bulles). En isolant
 * le shell ici, le canvas ne se démonte plus pendant qu'on navigue à l'intérieur
 * de la zone authentifiée → l'animation est continue d'une page à l'autre.
 *
 * Bonus : DRY. Les pages enfants n'ont plus à connaître la structure de la
 * sidebar / topbar — elles rendent juste leur contenu.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Suspense, useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { initiales as toInitiales, nomComplet as toNomComplet, roleLabel } from "../auth/format.js";
import { resoudreDomaineIdActif, resoudreMembershipActive } from "../auth/tenant-actif.js";
import { ME_QUERY_KEY, RequireAuth, useMe } from "../auth/use-me.js";
import { BubblesCanvas } from "../components/BubblesCanvas.js";
import { Sidebar } from "../components/layout/Sidebar.js";
import { Topbar } from "../components/layout/Topbar.js";
import { CommandPalette } from "../components/search/CommandPalette.js";
import { PageSkeleton } from "../components/ui/ListeSkeleton.js";
import { formatSurfaceHectares } from "../lib/format-surfaces.js";
import { appendNextIfMeaningful, buildNextParam } from "../lib/safe-next.js";
import { tenantQueryKey } from "../lib/tenant-query-key.js";
import { trpc } from "../lib/trpc.js";

export function AppShellLayout() {
  // RequireAuth garantit que `useMe()` a déjà résolu avec un user non-null
  // avant que Shell ne soit rendu — pas de guard nécessaire à l'intérieur.
  return (
    <RequireAuth>
      <WorkspaceTenantBoundary />
    </RequireAuth>
  );
}

/**
 * Remonte tout le shell authentifié quand le domaine résolu change.
 * Les formulaires, sélections, tiroirs et réponses de query de l'ancien
 * domaine sont ainsi démontés avant de rendre le nouveau contexte.
 */
function WorkspaceTenantBoundary() {
  const { data } = useMe();
  const domaineId = resoudreDomaineIdActif(data);
  return <Shell key={domaineId ?? "tenant-non-resolu"} />;
}

function Shell() {
  const { data } = useMe();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Invalide `me` à chaque changement de route interne. Sans ça, un user
  // suspendu pendant qu'il est sur le dashboard pourrait naviguer vers une
  // page sans tRPC (ex: /parametres index) sans déclencher la moindre 401 →
  // le polling 30s reste son seul filet, ce qui paraît cassé en interactif.
  // Coût : 1 appel `auth.me` par navigation interne (négligeable).
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
  }, [location.pathname, queryClient]);

  // Garanti par RequireAuth (cf commentaire ci-dessus).
  const me = data!;

  // CRITICAL : tous les hooks doivent être appelés AVANT les early returns
  // conditionnels (sinon "Rendered more hooks than during the previous render").
  // On lance la query parcellaire dès maintenant, le `enabled` se charge de
  // ne pas la déclencher si l'user n'a pas encore de tenant actif valide.
  // Résolution partagée avec la couche offline (clé de scoping du cache
  // parcelles) — cf `auth/tenant-actif.ts` (DRY).
  const activeMembership = resoudreMembershipActive(me);
  const domaineId = activeMembership?.domaine.id ?? null;
  const tenantPretAStats =
    activeMembership !== null &&
    activeMembership.statut_membership === "actif" &&
    activeMembership.domaine.statut !== "suspendu" &&
    activeMembership.domaine.onboarding_termine_le !== null &&
    !me.email_verification_required &&
    !me.mfa_setup_required &&
    me.permissions.includes("parcellaire.read");
  const { data: vignobleStats } = useQuery({
    queryKey: tenantQueryKey(domaineId, "parcellaire.statsResume"),
    queryFn: () => trpc.parcellaire.statsResume.query(),
    enabled: tenantPretAStats,
  });

  // Palette de recherche globale Ctrl+K (Lot H.5) : état + raccourci clavier
  // montés au niveau du shell (une seule instance pour toute l'app authentifiée).
  const [searchOpen, setSearchOpen] = useState(false);
  const searchTriggerRef = useRef<HTMLButtonElement>(null);

  // Tiroir de navigation (< lg). Au-dessus de `lg`, la barre latérale est
  // statique et cet état ne sert plus à rien.
  const [menuOuvert, setMenuOuvert] = useState(false);
  const contentScrollRef = useRef<HTMLDivElement>(null);
  // Toute navigation referme le tiroir : sans ça, on arrive sur la nouvelle
  // page avec le menu encore ouvert par-dessus. Le panneau de contenu est le
  // vrai conteneur scrollable (pas `window`) : il doit lui aussi revenir en
  // haut, sinon une route ouverte après une longue liste apparaît en plein
  // milieu de son écran — particulièrement déroutant sur mobile.
  useEffect(() => {
    setMenuOuvert(false);
    contentScrollRef.current?.scrollTo({ top: 0, left: 0 });
  }, [location.pathname]);
  // Échap referme, comme la palette de recherche.
  useEffect(() => {
    if (!menuOuvert) return;
    function onKeyDown(evenement: KeyboardEvent) {
      if (evenement.key === "Escape") setMenuOuvert(false);
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [menuOuvert]);
  useEffect(() => {
    function onKeyDown(evenement: KeyboardEvent) {
      // Ctrl+K (Windows/Linux) ou Cmd+K (macOS toléré) : ouvre / referme la palette.
      // preventDefault pour couper le comportement natif (focus barre d'adresse).
      if (
        (evenement.ctrlKey || evenement.metaKey) &&
        (evenement.key === "k" || evenement.key === "K")
      ) {
        evenement.preventDefault();
        setSearchOpen((ouvert) => !ouvert);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // Garde "email_verification_required" (feature email-verification) : si le
  // user n'a pas encore confirmé son email post-signup, on le force sur la
  // page d'attente dédiée. Posée AVANT MFA setup car la vérif email est plus
  // fondamentale (cf trpc.ts → emailVerifiedGuardMiddleware ordonné en
  // premier). Le backend rejette de toute façon toutes les routes métier
  // tant que `email_verifie_le IS NULL` → cette garde évite l'écran d'erreur.
  if (me.email_verification_required) {
    return <Navigate to="/confirmer-email-attente" replace />;
  }

  // Garde MFA setup obligatoire (Lot B.2.10) : un user multi-tenant sans MFA
  // n'a accès qu'à `/profile/mfa/setup`. Toutes les autres routes sous
  // AppShellLayout sont bloquées tant qu'il n'a pas activé sa 2FA. Côté
  // backend, le middleware `mfaSetupGuardMiddleware` rejetterait de toute
  // façon les calls métier — la garde frontend évite l'écran d'erreur.
  if (me.mfa_setup_required && location.pathname !== "/profile/mfa/setup") {
    return <Navigate to="/profile/mfa/setup?reason=multi_tenant" replace />;
  }

  const activeMemberships = me.domaines_accessibles.filter((m) => m.statut_membership === "actif");

  // Quand l'utilisateur est sur /profile/mfa/setup en mode "MFA setup required",
  // on court-circuite les gardes suivantes (« aucun domaine » / « multi-tenant
  // sans choix »). Sinon la garde multi-tenant le renverrait sur /choisir-domaine
  // — alors qu'il DOIT activer sa 2FA d'abord. Le tenant actif sera posé
  // après l'activation, par le flux normal post-MFA.
  if (!me.mfa_setup_required) {
    // Garde "aucun domaine accessible" : si l'utilisateur n'a aucune membership
    // active (suspendu de partout, parti de partout, ou uniquement des invites
    // pending), toutes les pages métier sous ce shell renverraient FORBIDDEN à
    // chaque appel tRPC. On le redirige vers une page sas dédiée — la session
    // reste valide, il peut juste se déconnecter ou attendre que son admin
    // réactive son accès.
    if (activeMemberships.length === 0) {
      return <Navigate to="/aucun-domaine" replace />;
    }

    // Décision 2026-05-14 : wizard d'onboarding accessible PENDANT
    // l'attente_validation pour que Max ait un dossier complet à examiner.
    // Bascule sur /compte-en-attente uniquement une fois le wizard finalisé.
    const allEnAttente = activeMemberships.every(
      (m) => m.domaine.statut === "en_attente_validation",
    );
    const allEnAttenteOnboarded = activeMemberships.every(
      (m) =>
        m.domaine.statut === "en_attente_validation" && m.domaine.onboarding_termine_le !== null,
    );
    const pathBypass =
      location.pathname === "/profile" ||
      location.pathname.startsWith("/profile/") ||
      location.pathname.startsWith("/onboarding");
    if (allEnAttenteOnboarded && !pathBypass) {
      return <Navigate to="/compte-en-attente" replace />;
    }
    if (allEnAttente && !allEnAttenteOnboarded && location.pathname === "/compte-en-attente") {
      return <Navigate to="/onboarding" replace />;
    }

    // Garde "choix de domaine pas encore fait" : un user multi-tenant qui vient
    // de se logger arrive ici sans `domaine_id_actif` (le backend ne pose plus
    // automatiquement la 1re membership pour éviter de polluer la colonne
    // `derniere_connexion_le`, cf login.usecase.ts). On le redirige vers la
    // page de choix tant qu'il n'a pas tranché.
    //
    // On propage `?next=` vers /choisir-domaine pour préserver la cible de
    // redirection initiale (cas typique : `/login?next=/parametres/membres`
    // → après login multi-tenant, on doit toujours arriver sur /parametres/membres
    // une fois le domaine choisi).
    if (!activeMembership && activeMemberships.length > 0) {
      const next = buildNextParam(location);
      return <Navigate to={appendNextIfMeaningful("/choisir-domaine", next)} replace />;
    }
  }

  // (activeMembership déjà calculé en haut, avant le useQuery, pour respecter
  // l'ordre des hooks. Pas de recalcul nécessaire ici.)

  // Tant que le wizard d'onboarding n'est pas finalisé, on enferme l'user
  // sur /onboarding (sauf /profile/* et /auth/*). S'applique aussi pendant
  // l'attente_validation pour que Max ait un dossier complet à examiner.
  if (
    activeMembership &&
    (activeMembership.domaine.statut === "actif" ||
      activeMembership.domaine.statut === "en_attente_validation") &&
    activeMembership.domaine.onboarding_termine_le === null &&
    location.pathname !== "/onboarding" &&
    !location.pathname.startsWith("/profile/") &&
    !location.pathname.startsWith("/auth/")
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  const utilisateur = {
    initiales: toInitiales(me.utilisateur.prenom, me.utilisateur.nom),
    nomComplet: toNomComplet(me.utilisateur.prenom, me.utilisateur.nom),
    role: roleLabel(activeMembership?.role_code ?? me.role_code_actif),
  };

  const domaineNom = activeMembership?.domaine.raison_sociale ?? "Domaine à configurer";
  // Plan d'abonnement : pas encore exposé par auth.me → "Cuvée" par défaut
  // (cf docs/commercial/pricing.md). À brancher quand le sélecteur sera prêt.
  const plan = activeMembership ? "Cuvée" : "Essai";

  // Surface vignoble : `vignobleStats` chargé en haut (hooks order strict).
  // Cache partagé avec Dashboard / Vignoble (même queryKey).
  const surfaceAffichee =
    vignobleStats && vignobleStats.nb_parcelles > 0
      ? formatSurfaceHectares(vignobleStats.surface_totale_ares)
      : undefined;

  // Sélecteur Topbar : memberships acceptées. Les domaines en
  // `en_attente_validation` sont affichés mais désactivés (libellé "En cours
  // de validation") pour que l'user sache qu'ils existent sans pouvoir
  // y switcher tant qu'ils ne sont pas validés.
  // On exclut les memberships pending (invitation pas encore acceptée).
  const tenantOptions = me.domaines_accessibles
    .filter((m) => m.statut_membership === "actif" && m.domaine.statut !== "suspendu")
    .map((m) => ({
      id: m.domaine.id,
      raison_sociale: m.domaine.raison_sociale,
      role_label: roleLabel(m.role_code),
      pending_validation: m.domaine.statut === "en_attente_validation",
    }));

  return (
    // Hauteur disponible sous le bandeau de RequireAuth (pas min-h) → la fenêtre
    // ne scroll pas, seul le contenu défile (la sidebar reste fixe).
    //
    // `min-w-[1024px]` est conditionné à `lg` depuis le 2026-07-26 : il
    // s'appliquait à TOUTES les largeurs, donc les 109 routes authentifiées
    // débordaient de `1024 − largeur` sur téléphone et tablette (634 px sur un
    // écran de 390). Le comportement bureau est inchangé.
    <>
      <div className="dashboard-bg flex h-full text-champagne-900 lg:min-w-[1024px]">
        {/* Barre latérale : dans le flux à partir de lg seulement. En dessous,
            c'est le tiroir monté plus bas qui prend le relais — sinon elle
            mangeait 240 px des 390 px de l'écran. */}
        <div className="hidden lg:block">
          <Sidebar domaine={{ nom: domaineNom, surface: surfaceAffichee }} plan={plan} />
        </div>
        {/* Colonne droite en `relative` pour servir d'ancrage au canvas de bulles
          (qui s'étendra sur toute cette zone, sidebar exclue). */}
        <div className="relative flex min-w-0 flex-1 flex-col">
          {/* Bulles dorées translucides en fond — densité moitié de la page de
            connexion (50 vs 90) pour rester subtil sur un écran de travail. */}
          <BubblesCanvas count={50} tone="light" />

          {/* Topbar + zone scrollable doivent passer au-dessus du canvas → on
            les met en `relative` pour créer leur propre stacking context. */}
          <div className="relative">
            <Topbar
              tenants={tenantOptions}
              activeTenantId={me.domaine_id_actif}
              utilisateur={utilisateur}
              // TODO V1 : brancher sur notification.unreadCount quand le module
              // notifications sera en place. Pour l'instant, 0 → badge masqué.
              notificationsCount={0}
              onOpenSearch={() => setSearchOpen(true)}
              searchButtonRef={searchTriggerRef}
              onOuvrirMenu={() => setMenuOuvert(true)}
            />
          </div>
          <div ref={contentScrollRef} className="relative flex-1 overflow-x-hidden overflow-y-auto">
            {/* Suspense DANS le shell : pendant le chargement d'un chunk de route
              lazy, seul ce panneau contenu affiche un squelette — la sidebar,
              la topbar et l'animation des bulles restent montées et continues. */}
            <Suspense fallback={<PageSkeleton />}>
              <Outlet />
            </Suspense>
          </div>
        </div>

        {/* Palette de recherche globale (Ctrl+K) — montée une fois pour toute
            l'app authentifiée, au-dessus de tout le chrome. */}
        <CommandPalette
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          domaineId={activeMembership?.domaine.id ?? null}
          triggerRef={searchTriggerRef}
        />
      </div>

      {/* ── Tiroir de navigation (< lg) ────────────────────────────────────
          Monté HORS de `.dashboard-bg` : la règle `.dashboard-bg > *` force
          `z-index: 1` en CSS non-layerisé, ce qui bat les utilitaires Tailwind
          — un tiroir placé dedans serait passé SOUS le contenu malgré son
          `z-50`. Il reste monté en permanence (translaté hors écran quand il
          est fermé) : la page en dessous n'est jamais démontée, donc l'état
          local d'un formulaire de pesée survit à l'ouverture du menu. */}
      {menuOuvert && (
        <button
          type="button"
          aria-label="Fermer le menu"
          onClick={() => setMenuOuvert(false)}
          // `aubergine-950` n'existe pas dans la palette (elle s'arrête à 900) :
          // Tailwind ne générait aucune règle et le voile était transparent —
          // rien ne signalait que le menu était modal.
          className="fixed inset-0 z-40 bg-aubergine-900/40 lg:hidden"
        />
      )}
      <div
        className={`fixed inset-y-0 left-0 z-50 transition-transform duration-200 lg:hidden ${
          menuOuvert ? "translate-x-0" : "-translate-x-full"
        }`}
        // Masqué au clavier et aux lecteurs d'écran tant qu'il est fermé :
        // sans ça, la tabulation traverse un menu invisible.
        inert={!menuOuvert}
      >
        <Sidebar domaine={{ nom: domaineNom, surface: surfaceAffichee }} plan={plan} />
      </div>
    </>
  );
}
